import React, { useState, useEffect } from "react";
import { motion } from 'framer-motion';
import { FiPlay, FiX } from 'react-icons/fi';
import { SectionHeader } from './ui';
import '../assets/styles/AboutMe.scss';
import { trackSectionView, trackEvent } from '../utils/mixpanel';

const highlights = [
  { value: '8+', label: 'Years Experience', icon: '🧠' },
  { value: '3', label: 'Mobile Platforms', icon: '📱' },
  { value: 'Full', label: 'Stack Engineer', icon: '⚙️' },
  { value: 'AI', label: 'Powered Solutions', icon: '🤖' },
];

function AboutMe() {
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  useEffect(() => {
    trackSectionView('about-me');
  }, []);

  const handleVideoClick = () => {
    setIsVideoOpen(true);
    trackEvent('Video Played', {
      video_title: 'About Me Video',
      video_url: 'https://www.youtube.com/embed/dA0Qq1yi4-8'
    });
  };

  const handleCloseModal = () => {
    setIsVideoOpen(false);
  };

  return (
    <section className="about" id="about-me">
      <div className="about__container">
        <SectionHeader
          title="About Me"
          align="left"
        />

        <div className="about__grid">
          {/* Text Side */}
          <motion.div
            className="about__text"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.5 }}
          >
            <p className="about__para">
              Seasoned Senior Software Engineer with over 8 years of experience, I specialize in engineering high-performance mobile apps, scalable backend systems, and AI-powered solutions. My career is built on native Android mastery, now evolved into a full-stack approach that bridges seamless mobile experiences with robust server-side infrastructure and intelligent systems.
            </p>
            <p className="about__para">
              I am deeply proficient in Kotlin and Swift, advocating for modern declarative UIs with Jetpack Compose and SwiftUI. Beyond mobile, I design and scale backend systems using Laravel, building secure RESTful APIs, Shopify embedded apps, and implementing CI/CD pipelines, Docker, and cloud infrastructure on AWS and Azure.
            </p>
            <p className="about__para">
              I operate as an AI-augmented engineer — leveraging Claude Code and Cursor to run agentic workflows that automate testing, continuous code review, and deployment pipelines. This isn't just autocomplete; it's orchestrating AI to ship faster, reduce review cycles, and maintain quality at scale.
            </p>
            <p className="about__para">
              An advocate for Agile methodologies, I take pride in mentoring junior developers, conducting rigorous code reviews, and fostering a culture of technical excellence across every project I lead.
            </p>
          </motion.div>

          {/* Video Side */}
          <motion.div
            className="about__video-side"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="about__video-wrapper" onClick={handleVideoClick}>
              <img
                src="https://i3.ytimg.com/vi/dA0Qq1yi4-8/hqdefault.jpg"
                alt="Click to play video"
                className="about__video-thumb"
              />
              <div className="about__video-overlay">
                <div className="about__play-btn">
                  <div className="about__play-icon">
                    <FiPlay />
                  </div>
                  <span className="about__play-label">Watch My Story</span>
                </div>
              </div>
            </div>
            <div className="about__video-caption">
              <span className="about__video-caption-line" />
              <span className="about__video-caption-text">Click to watch — I explain my skills & experience in 1 min</span>
              <span className="about__video-caption-line" />
            </div>

            {/* Highlight Cards */}
            <div className="about__highlights">
              {highlights.map((h, index) => (
                <motion.div
                  key={h.label}
                  className="about__highlight"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.08 }}
                >
                  <span className="about__highlight-icon">{h.icon}</span>
                  <span className="about__highlight-value">{h.value}</span>
                  <span className="about__highlight-label">{h.label}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Video Modal */}
      {isVideoOpen && (
        <motion.div
          className="about__modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={handleCloseModal}
        >
          <motion.div
            className="about__modal-content"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button className="about__modal-close" onClick={handleCloseModal}>
              <FiX />
            </button>
            <iframe
              width="100%"
              height="100%"
              src="https://www.youtube.com/embed/dA0Qq1yi4-8?autoplay=1"
              title="About Me Video"
              frameBorder="0"
              allow="autoplay; encrypted-media"
              allowFullScreen
            />
          </motion.div>
        </motion.div>
      )}
    </section>
  );
}

export default AboutMe;
